import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Sparkles,
  Recycle,
  PackageCheck,
  Globe2,
  Layers,
  ShieldCheck,
  Flame,
  Droplets,
  Leaf,
  CheckCircle2,
} from "lucide-react";
import { Eyebrow, SectionHeading, FadeUp, Pill } from "../components/site/Primitives";
import ProductCard from "../components/site/ProductCard";
import SceneImage from "../components/site/SceneImage";
import { useProducts } from "../hooks/useProducts";

const TRUSTED = [
  "Restaurants",
  "Caterers",
  "Hotels",
  "Food Trucks",
  "Ghost Kitchens",
  "Meal-Prep Brands",
  "Supermarkets",
  "Street Food",
  "Private Label",
  "Retail Chains",
];

const WHY = [
  {
    icon: Leaf,
    title: "Made for African food",
    body: "Bowls, trays and bags engineered for jollof, fufu, light soup, waakye and the dishes that actually fill takeaway counters across Ghana.",
  },
  {
    icon: PackageCheck,
    title: "Restaurant-grade performance",
    body: "Leak resistant, oil & water resistant, microwave safe and structurally rigid. Packaging that protects the meal — and the brand serving it.",
  },
  {
    icon: Layers,
    title: "Private-label ready",
    body: "Print your logo, story and reorder QR on kraft sleeves, food bags and bespoke cartons. Look premium, stay sustainable.",
  },
  {
    icon: Globe2,
    title: "Built for the transition",
    body: "Ghana's Styrofoam ban begins January 2027. We are helping food businesses move now — before the deadline forces a scramble.",
  },
];

const QUALITY = [
  { icon: Leaf, label: "100% Compostable" },
  { icon: ShieldCheck, label: "PFAS Free" },
  { icon: CheckCircle2, label: "Food Grade Certified" },
  { icon: Flame, label: "Microwave Safe" },
  { icon: Droplets, label: "Oil & Water Resistant" },
  { icon: PackageCheck, label: "Strong & Leak Proof" },
];

export default function HomePage() {
  const { products } = useProducts();
  return (
    <div className="bg-[#0A0A09]">
      {/* ===== HERO ===== */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden min-h-[92vh] flex items-end pt-12 pb-20 lg:pb-28 kraft-texture"
      >
        <div className="absolute inset-0 -z-10">
          <SceneImage
            scene="hero-counter"
            eager
            className="h-full w-full object-cover"
            alt="A customer collects a takeaway order at a modern Ghanaian counter."
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A09] via-[#0A0A09]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A09]/95" />
        </div>

        <div className="mx-auto max-w-[1400px] w-full px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <FadeUp>
              <div className="inline-flex items-center gap-3 rounded-full border border-[#E85D2F]/30 bg-[#E85D2F]/10 px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-[#E85D2F] font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-[#E85D2F] animate-pulse" />
                A BlacVerse Group Company
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h1 className="mt-8 font-grotesk text-[52px] sm:text-7xl lg:text-[112px] font-black tracking-[-0.045em] leading-[0.9] text-[#F7F5F0]">
                Packaging
                <br />
                the Future
                <span className="text-[#E85D2F]">.</span>
              </h1>
            </FadeUp>
            <FadeUp delay={0.25}>
              <p className="mt-8 text-base md:text-xl text-[#F7F5F0]/80 leading-relaxed max-w-2xl font-light">
                Africa&rsquo;s leading compostable food-packaging company.
                Bowls, trays, bags and cutlery built for restaurants, street
                food vendors, caterers and food businesses ready for what comes
                next.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <p className="mt-6 font-grotesk text-sm tracking-[0.3em] uppercase text-[#E85D2F]/90 font-semibold">
                Sustainable · Practical · Affordable · Scalable
              </p>
            </FadeUp>
            <FadeUp delay={0.4}>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-quote"
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-8 py-4 font-semibold tracking-wide hover:bg-[#F47A4A] transition-colors"
                >
                  Request a Quote
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/products"
                  data-testid="hero-cta-products"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-[#F7F5F0]/30 text-[#F7F5F0] px-8 py-4 font-medium tracking-wide hover:bg-[#F7F5F0] hover:text-[#0A0A09] transition-colors"
                >
                  View Products
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.5} className="lg:col-span-4 hidden lg:block">
            <div className="rounded-3xl bg-[#121211]/80 backdrop-blur border border-[#E85D2F]/20 p-7">
              <Pill>
                <Sparkles className="h-3 w-3" /> The change is coming
              </Pill>
              <p className="mt-6 font-grotesk text-5xl font-black tracking-tighter text-[#F7F5F0]">
                Jan
                <span className="text-[#E85D2F]">.</span>
              </p>
              <p className="mt-1 font-grotesk text-5xl font-black tracking-tighter text-[#F7F5F0]">
                2027
              </p>
              <p className="mt-4 text-sm text-[#F7F5F0]/70 leading-relaxed">
                Ghana begins its transition away from Styrofoam food packaging.
                Early movers will lead.
              </p>
              <Link
                to="/sustainability"
                className="mt-6 inline-flex items-center gap-2 text-[#E85D2F] text-sm font-medium hover:text-[#F47A4A] transition-colors"
              >
                Read the brief
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ===== TRUSTED MARQUEE ===== */}
      <section data-testid="trusted-section" className="py-16 border-y border-[#F7F5F0]/10 bg-[#0A0A09]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 mb-8">
          <Eyebrow>Trusted by food businesses across Ghana</Eyebrow>
        </div>
        <div className="overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap gap-24 text-5xl md:text-7xl font-grotesk font-black tracking-tighter">
            {[...TRUSTED, ...TRUSTED].map((t, i) => (
              <span
                key={i}
                className="text-[#F7F5F0]/10 hover:text-[#E85D2F]/40 transition-colors"
              >
                {t}<span className="text-[#E85D2F] mx-12">●</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CREAM SECTION: Sustainable. Practical. Affordable. Scalable. ===== */}
      <section className="bg-[#F2EEE3] text-[#0A0A09] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#E85D2F]">
              <span className="h-px w-8 bg-[#E85D2F]" />
              Our solution
            </span>
            <h2 className="mt-6 font-grotesk text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.04em] leading-[0.92]">
              Sustainable.
              <br />
              Practical.
              <br />
              <span className="text-[#E85D2F]">Affordable.</span>
              <br />
              Scalable.
            </h2>
            <p className="mt-8 max-w-md text-[#0A0A09]/70 leading-relaxed text-lg font-light">
              Made for African food. Made for the future. Built for every food
              business — from a single jollof spot to a national restaurant
              chain.
            </p>
          </div>
          <FadeUp className="lg:col-span-6">
            <div className="relative rounded-3xl overflow-hidden aspect-[5/4] bg-[#E8E0CF]">
              <SceneImage
                scene="bowl-jollof"
                className="absolute inset-0 h-full w-full object-cover"
                alt="BlacPac compostable bagasse bowl filled with Ghanaian jollof rice."
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ===== FEATURED PRODUCTS ===== */}
      <section data-testid="featured-products" className="py-24 lg:py-32 kraft-texture">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <SectionHeading
              eyebrow="Our products"
              title="More products. More solutions. Built for Africa."
              kicker="Six everyday workhorses across our Soup, Rice, Street Food and Essentials ranges — every line is wholesale-ready and private-label-ready."
            />
            <Link
              to="/products"
              data-testid="view-all-products"
              className="group inline-flex items-center gap-3 text-[#E85D2F] font-medium hover:text-[#F47A4A] transition-colors whitespace-nowrap"
            >
              View full catalogue
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {products.map((p, i) => (
              <FadeUp key={p.id} delay={i * 0.08}>
                <ProductCard product={p} index={i} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ===== QUALITY STANDARDS BANNER ===== */}
      <section data-testid="quality-standards" className="bg-[#F2EEE3] text-[#0A0A09] py-20 lg:py-24 border-y border-[#0A0A09]/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
            <div>
              <span className="inline-flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#E85D2F]">
                <span className="h-px w-8 bg-[#E85D2F]" />
                Quality standards
              </span>
              <h2 className="mt-6 font-grotesk text-4xl md:text-5xl font-black tracking-tighter leading-[0.95] max-w-2xl">
                Six standards. Every product. Every order.
              </h2>
            </div>
            <p className="text-[#0A0A09]/65 max-w-md font-light leading-relaxed">
              Our range is built to a single quality bar — so you can switch
              your kitchen with confidence.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {QUALITY.map((q) => (
              <div
                key={q.label}
                className="flex flex-col items-start gap-3 rounded-2xl bg-[#0A0A09] text-[#F7F5F0] p-5 hover:bg-[#1A1A18] transition-colors"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#E85D2F] text-[#0A0A09]">
                  <q.icon className="h-5 w-5" strokeWidth={2.2} />
                </span>
                <p className="font-grotesk font-bold text-sm leading-tight">
                  {q.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRIVATE LABEL SPOTLIGHT ===== */}
      <section data-testid="private-label-spotlight" className="py-24 lg:py-32 bg-[#0A0A09] border-y border-[#F7F5F0]/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <FadeUp className="lg:col-span-6">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
              <SceneImage
                scene="private-label-row"
                className="absolute inset-0 h-full w-full object-cover"
                alt="A row of BlacPac kraft food bags lined up on a restaurant counter."
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A09]/60 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8 flex items-end justify-between">
                <div>
                  <p className="font-grotesk text-sm uppercase tracking-[0.22em] text-[#E85D2F] font-semibold">
                    Your brand, beautifully wrapped
                  </p>
                  <p className="font-grotesk font-black text-[#F7F5F0] text-3xl tracking-tighter mt-2">
                    PRIVATE LABEL<span className="text-[#E85D2F]">.</span>
                  </p>
                </div>
              </div>
            </div>
          </FadeUp>

          <div className="lg:col-span-6">
            <FadeUp>
              <Eyebrow>Private-label packaging</Eyebrow>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-grotesk text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-[#F7F5F0]">
                Make every meal a brand moment.
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-6 text-base md:text-lg text-[#F7F5F0]/70 leading-relaxed font-light max-w-xl">
                We print logos, stories and reorder QRs onto kraft sleeves, food
                bags and bespoke bowls. Built for restaurants, supermarket
                brands, hotels and food chains who want packaging that looks as
                considered as the food inside.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <ul className="mt-8 grid grid-cols-2 gap-3 text-sm text-[#F7F5F0]/85">
                {["Custom kraft sleeves", "Logo printing", "Branded stickers", "Bulk supply", "Monthly reorders", "Packaging consultation"].map((x) => (
                  <li key={x} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#E85D2F]" />
                    {x}
                  </li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.4}>
              <Link
                to="/private-label"
                data-testid="private-label-cta"
                className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-7 py-4 font-semibold tracking-wide hover:bg-[#F47A4A] transition-colors group"
              >
                Create your branded line
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ===== WHY BLACPAC ===== */}
      <section data-testid="why-blacpac" className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <SectionHeading
            eyebrow="Why BlacPac"
            title="The change is coming. Move before the deadline."
            kicker="A serious supplier for serious food businesses — combining product quality, brand-grade aesthetics and dependable monthly supply."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => (
              <FadeUp key={w.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-[#E85D2F]/15 bg-[#121211] p-7 hover:border-[#E85D2F]/40 hover:-translate-y-1 transition-all duration-500">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E85D2F] text-[#0A0A09]">
                    <w.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-grotesk text-xl font-bold text-[#F7F5F0] tracking-tight">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#F7F5F0]/65 leading-relaxed">
                    {w.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SUSTAINABILITY PROMISE ===== */}
      <section
        data-testid="sustainability-promise"
        className="relative py-24 lg:py-32 bg-[#1A1A18] overflow-hidden border-y border-[#F7F5F0]/5"
      >
        <div className="absolute inset-0 opacity-20">
          <SceneImage
            scene="compost-return"
            className="h-full w-full object-cover"
            alt=""
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A18] via-[#1A1A18]/90 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>From nature. Back to nature.</Eyebrow>
            <h2 className="mt-6 font-grotesk text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-[#F7F5F0]">
              Made from renewable plant materials that{" "}
              <span className="text-[#E85D2F]">return to the earth</span>{" "}
              without leaving harm behind.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#F7F5F0]/80 leading-relaxed font-light max-w-2xl">
              Sugarcane bagasse, unbleached kraft paper and FSC-aligned
              birchwood — designed to support compostable and lower-plastic
              packaging goals for African food businesses.
            </p>
            <Link
              to="/sustainability"
              data-testid="sustainability-cta"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#F7F5F0] text-[#0A0A09] px-7 py-4 font-semibold tracking-wide hover:bg-[#E85D2F] transition-colors group"
            >
              Our materials & approach
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <FadeUp className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Primary material", value: "Sugarcane bagasse" },
                { label: "Finish", value: "Unbleached natural" },
                { label: "Birchwood cutlery", value: "Home compostable" },
                { label: "Plastic dependency", value: "Eliminated" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl border border-[#F7F5F0]/15 bg-[#0A0A09]/60 backdrop-blur p-5"
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E85D2F] font-semibold">
                    {s.label}
                  </p>
                  <p className="mt-2 font-grotesk text-xl font-bold text-[#F7F5F0] tracking-tight">
                    {s.value}
                  </p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ===== WHOLESALE ===== */}
      <section data-testid="wholesale-section" className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>Wholesale supply</Eyebrow>
            <h2 className="mt-6 font-grotesk text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-[#F7F5F0]">
              A recurring supply partner — not a one-off vendor.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <p className="text-base md:text-lg text-[#F7F5F0]/70 leading-relaxed font-light">
              BlacPac is structured for predictable monthly volumes.
              Container-load imports starting with a 20ft container of assorted
              SKUs, local stockholding in Accra and bespoke packaging
              solutions — so you can plan, scale and grow without packaging
              becoming an operational headache.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {[
                "Bulk ordering",
                "Monthly recurring supply",
                "20ft / 40ft container imports",
                "Stock planning & forecasting",
                "Ghana market focus",
                "Bespoke packaging solutions",
              ].map((x) => (
                <div
                  key={x}
                  className="flex items-center gap-3 rounded-2xl border border-[#F7F5F0]/10 bg-[#121211] px-5 py-4"
                >
                  <Recycle className="h-4 w-4 text-[#E85D2F] shrink-0" />
                  <span className="text-sm text-[#F7F5F0]/85">{x}</span>
                </div>
              ))}
            </div>
            <Link
              to="/wholesale"
              data-testid="wholesale-cta"
              className="mt-10 inline-flex items-center gap-3 rounded-full border border-[#E85D2F]/50 text-[#E85D2F] px-7 py-4 font-medium tracking-wide hover:bg-[#E85D2F] hover:text-[#0A0A09] transition-colors group"
            >
              Request wholesale pricing
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section data-testid="final-cta" className="pb-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="relative overflow-hidden rounded-[2rem] bg-[#F2EEE3] text-[#0A0A09] p-10 md:p-16 lg:p-20">
            <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-[#E85D2F]/30 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-[#E85D2F]/15 blur-3xl" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#E85D2F] font-semibold">
                  Let&rsquo;s build a cleaner future together.
                </p>
                <h2 className="mt-6 font-grotesk text-4xl md:text-5xl lg:text-7xl font-black tracking-[-0.04em] leading-[0.9]">
                  Build your sustainable packaging supply with{" "}
                  <span className="text-[#E85D2F]">BlacPac.</span>
                </h2>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <Link
                  to="/contact"
                  data-testid="final-cta-quote"
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-[#0A0A09] text-[#F7F5F0] px-8 py-5 font-semibold tracking-wide hover:bg-[#1A1A18] transition-colors group"
                >
                  Request a Quote
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/products"
                  data-testid="final-cta-catalogue"
                  className="inline-flex items-center justify-center gap-3 rounded-full border border-[#0A0A09]/30 text-[#0A0A09] px-8 py-5 font-medium tracking-wide hover:bg-[#0A0A09] hover:text-[#F7F5F0] transition-colors"
                >
                  Browse Catalogue
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
